import { useState } from 'react'
import { queryClient } from '~/common/react-query/queryClient'
import { Box, Stack, Text, Card, CardBody, CardHeader, CardHeading, CardActionLink, RegionError } from '~/design_system'
import { useHomeConfigQuery } from '~/api/home'
import { REPORTS_QUERY_KEY, useGetFavoriteReportsQuery } from '~/api/reports'
import { SadStates } from '~/components/SadStates'
import { CountPager } from '~/components/v2/CountPager'
import { ItemsList, ItemsListFooter, ItemsListItem, ItemsListItems, ItemsListLink } from '~/components/v2/ItemsList'
import { MutedSkeleton, WidgetEmptyState } from '~/home/common'
import { HeartO } from '~/icons'
import { useMsg } from '~/common/localizationUtils'
import { replaceReportUrl } from '~/custom_reports/home/ReportItem'

const FAVORITE_REPORTS_COUNT = 5

export const FavoriteReports = props => {
  const msg = useMsg()
  const { data: homeConfig } = useHomeConfigQuery()

  const [paginationParams, setPaginationParams] = useState({
    limit: FAVORITE_REPORTS_COUNT,
    offset: 0
  })

  const hasFavoriteReportAccess = Boolean(homeConfig?.reports?.can_access_favorite_reports)
  const {
    data = {},
    isError,
    isLoading,
    isFetching
  } = useGetFavoriteReportsQuery({ ...paginationParams }, { enabled: hasFavoriteReportAccess })

  const {
    results: favoriteReports,
    page,
    total_pages: totalPages,
    next_params: nextParams,
    previous_params: previousParams
  } = data

  return hasFavoriteReportAccess ? (
    <Card data-testid="favorite-reports-list" {...props}>
      <CardHeader>
        <CardHeading>{msg('favorite_reports.heading')}</CardHeading>
        <CardActionLink href="/reporting?ordering=newest&favorites=true">{msg('favorite_reports.link')}</CardActionLink>
      </CardHeader>
      <CardBody p={0}>
        <ItemsList border={0}>
          <SadStates
            states={[
              {
                when: isError,
                render: () => (
                  <RegionError
                    onRefresh={() =>
                      queryClient.invalidateQueries([REPORTS_QUERY_KEY, { ...paginationParams, favorites: true }])
                    }
                  />
                )
              },
              {
                when: isLoading,
                render: () => <FavoriteReportSkeleton />
              },
              {
                when: !isLoading && !isFetching && favoriteReports?.length === 0,
                render: () => (
                  <WidgetEmptyState icon={HeartO} m={0}>
                    <Text>{msg('favorite_reports.no_favorites')}</Text>
                  </WidgetEmptyState>
                )
              }
            ]}>
            {() => (
              <ItemsListItems>
                {favoriteReports?.map(item => (
                  <FavoriteReport key={`${item.report_type}${item.id}`} {...item} />
                ))}
              </ItemsListItems>
            )}
          </SadStates>
          {totalPages > 1 && (
            <ItemsListFooter py={3} justify="center">
              <CountPager
                page={page}
                totalPages={totalPages}
                onNextPage={() => setPaginationParams(nextParams)}
                onPrevPage={() => setPaginationParams(previousParams)}
              />
            </ItemsListFooter>
          )}
        </ItemsList>
      </CardBody>
    </Card>
  ) : (
    <></>
  )
}

const FavoriteReport = ({ name, description, ...rest }) => {
  const url = replaceReportUrl(rest)
  return (
    <ItemsListItem py={6}>
      <ItemsListLink href={url}>
        <Stack>
          <Stack isInline spacing={2}>
            <Box as={HeartO} color="#F56565" alignSelf="center" />
            <Text data-testid={name} fontWeight="semibold">
              {name}
            </Text>
          </Stack>
          {description && (
            <Text ml={8} fontSize="sm" wordBreak="break-word">
              {description}
            </Text>
          )}
        </Stack>
      </ItemsListLink>
    </ItemsListItem>
  )
}

const FavoriteReportSkeleton = () => (
  <ItemsListItems maxH={500} overflowY="auto">
    {Array(FAVORITE_REPORTS_COUNT)
      .fill(undefined)
      .map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <ItemsListItem py={6} key={index}>
          <Stack isInline width="full">
            <MutedSkeleton height="24px" width="24px" />
            <Stack>
              <MutedSkeleton width="180px" height="24px" />
              <MutedSkeleton width="240px" height="14px" />
            </Stack>
          </Stack>
        </ItemsListItem>
      ))}
  </ItemsListItems>
)
